import React, { useMemo, useState } from 'react'
import { useChartExport } from '../../hooks/useChartExport'
import type { FfpDashboardData, MeasureKey, Movement, PeriodKey, Progress as ProgressData } from '../../types'
import { monthOf, monthRange, peopleHave, sincePhrase } from '../../utils'
import { AboutPanel, PanelFreshness, PanelNote, PanelSection, PanelTable, PanelText } from '../AboutPanel'
import { Card } from '../Card'
import { SplitBars } from '../charts/SplitBars'
import type { Segment } from '../charts/SplitBars'
import { COLOURS } from '../charts/theme'
import { ProgressEarlyDays } from '../EarlyDays'
import { FRESHNESS_SOURCE } from '../../copy'
import { PanelIncluded } from '../PanelIncluded'
import { Reveal } from '../Reveal'
import { SectionHeader } from '../SectionHeader'
import type { Intro } from '../SectionHeader'
import styles from './progress.module.sass'

type Props = {
  data: FfpDashboardData
  // Unused: progress is always first capture to latest, whatever the period.
  period: PeriodKey
}

type MeasureRow = MeasureKey | 'retirement'
type Panel = 'movement' | 'outcomes'

const ROWS: MeasureRow[] = ['retirement', 'debt', 'spending', 'investing']

const ROW_NAMES: Record<MeasureRow, string> = {
  retirement: 'Retirement readiness',
  debt: 'Debt repayments',
  spending: 'Spending',
  investing: 'Investing',
}

const PANEL_TITLES: Record<Panel, string> = { movement: 'Who has moved', outcomes: 'What has changed so far' }

// The server hides a measure or outcome counted from fewer than 5 people, but
// shows small counts inside a bigger group, unlike the default note.
const COUNTS_NOTE =
  'A measure or outcome counted from fewer than 5 people is hidden. Within a bigger group small counts are shown: they are totals of people, not a figure about any one person.'

// "64 started above the 10% guide"
const startedBehind = (row: MeasureRow, movement: Movement | null, data: FfpDashboardData): string => {
  if (movement === null) return 'Hidden: fewer than 5 started behind'
  const count = movement.started_behind
  if (row === 'retirement') return `${count} started Unlikely or lower`
  const { guide } = data.health.trends[row]
  return row === 'investing' ? `${count} started at or below the ${guide}% guide` : `${count} started above the ${guide}% guide`
}

// "Debt is improving fastest: 26 of 64 people are now within the guide"
const FASTEST: Record<MeasureRow, (improved: number, behind: number) => string> = {
  retirement: (improved, behind) => `Retirement readiness is improving fastest: ${improved} of ${behind} people have moved up a band`,
  debt: (improved, behind) => `Debt is improving fastest: ${improved} of ${behind} people are now within the guide`,
  spending: (improved, behind) => `Spending is improving fastest: ${improved} of ${behind} people are now within the guide`,
  investing: (improved, behind) => `Investing is improving fastest: ${improved} of ${behind} people are now within the guide`,
}

const total = ({ improved, no_change: same, moved_down: down }: Movement): number => improved + same + down

// The measures the server shows, in row order (hidden ones are null).
const shownMeasures = (progress: ProgressData): [MeasureRow, Movement][] =>
  ROWS.flatMap((row) => {
    const movement = progress.measures[row]
    return movement ? [[row, movement] as [MeasureRow, Movement]] : []
  })

// Named after the measure with the largest share moving up, when more people
// moved up than down overall.
const movementTitle = (progress: ProgressData, firstMonth: string | null): string => {
  const shown = shownMeasures(progress)
  const up = shown.reduce((sum, [, movement]) => sum + movement.improved, 0)
  const down = shown.reduce((sum, [, movement]) => sum + movement.moved_down, 0)
  const ranked = shown
    .filter(([, movement]) => movement.started_behind > 0)
    .sort(([, a], [, b]) => b.improved / b.started_behind - a.improved / a.started_behind)
  if (up <= down || ranked.length === 0) return `Who has moved ${sincePhrase(firstMonth)}`

  const [row, { improved, started_behind: behind }] = ranked[0]
  return FASTEST[row](improved, behind)
}

const progressIntro = ({ people, progress }: FfpDashboardData): Intro => {
  if (people.early_days || progress.people_counted === 0) return { before: 'It’s ', highlight: 'too early', after: ' to see movement yet.' }
  const shown = shownMeasures(progress)
  if (shown.length === ROWS.length && shown.every(([, movement]) => movement.improved > movement.moved_down)) {
    return { before: 'More people are ', highlight: 'moving up than down', after: ' on every measure.' }
  }
  return { before: 'Each row follows the same people from their first month to their latest.', highlight: '', after: '' }
}

// "Harbour Logistics · Feb to Sep 2026 · n = 145 people with 2+ months of data"
const progressFooter = (data: FfpDashboardData) => {
  const latest = data.freshness.captured_at ? monthOf(data.freshness.captured_at) : null
  const parts = [data.organization.name]
  if (data.first_month && latest) parts.push(monthRange(data.first_month, latest))
  parts.push(`n = ${data.progress.people_counted} people with 2+ months of data`)
  const waiting = data.progress.not_yet_included
  return { text: parts.join(' · '), chip: waiting > 0 ? `${waiting} not included yet` : 'Everyone included' }
}

export const Progress = ({ data }: Props) => {
  const [panel, setPanel] = useState<Panel | null>(null)
  const closePanel = () => setPanel(null)
  const waiting = data.people.early_days || data.progress.people_counted === 0
  const content = waiting ? <ProgressEarlyDays threshold={data.people.early_days_threshold} /> : <ProgressCards data={data} panel={panel} onPanel={setPanel} />

  return (
    <section id="progress" className={styles.section}>
      <SectionHeader title="How the programme is moving your team" intro={progressIntro(data)} />
      {content}
      <AboutPanel open={panel !== null} title={panel ? PANEL_TITLES[panel] : ''} onClose={closePanel}>
        <ProgressPanelContent panel={panel} data={data} />
      </AboutPanel>
    </section>
  )
}

type CardsProps = {
  data: FfpDashboardData
  panel: Panel | null
  onPanel: (panel: Panel) => void
}

const ProgressCards = ({ data, panel, onPanel }: CardsProps) => (
  <div className={styles.grid}>
    <MovementCard data={data} highlighted={panel === 'movement'} onAbout={() => onPanel('movement')} />
    <OutcomesCard data={data} highlighted={panel === 'outcomes'} onAbout={() => onPanel('outcomes')} />
  </div>
)

type CardProps = {
  data: FfpDashboardData
  highlighted: boolean
  onAbout: () => void
}

const share = (part: number, whole: number): number => (whole > 0 ? (part / whole) * 100 : 0)

// Each row is its own whole: the people who started behind on that measure.
const MovementCard = ({ data, highlighted, onAbout }: CardProps) => {
  const { chartRef, exportImage } = useChartExport<'bar'>()
  const { measures } = data.progress
  const rows = useMemo(() => ROWS.map((row) => [ROW_NAMES[row], startedBehind(row, measures[row], data)]), [measures, data])
  const segments: Segment[] = useMemo(() => {
    // A hidden measure draws an empty row.
    const segment = (name: string, colour: string, key: 'improved' | 'no_change' | 'moved_down'): Segment => ({
      name,
      colour,
      text: COLOURS.ink,
      values: ROWS.map((row) => {
        const movement = measures[row]
        return movement ? share(movement[key], total(movement)) : 0
      }),
      captions: ROWS.map((row) => {
        const movement = measures[row]
        return movement ? String(movement[key]) : ''
      }),
    })
    return [
      segment('Moved up or now within the guide', COLOURS.lime, 'improved'),
      segment('No change yet', COLOURS.greyBar, 'no_change'),
      segment('Moved down', COLOURS.orange, 'moved_down'),
    ]
  }, [measures])

  return (
    <Reveal>
      <Card
        question="Is the programme moving people forward?"
        title={movementTitle(data.progress, data.first_month)}
        onAbout={onAbout}
        highlighted={highlighted}
        exportImage={exportImage}
        exportName="Who has moved"
        footer={progressFooter(data)}
      >
        <p className={styles.metric}>People who started behind on each measure, and where they are now</p>
        <Legend />
        <SplitBars ref={chartRef} rows={rows} segments={segments} rowHeight={62} label="People who started behind on each measure: how many moved up, did not change or moved down" />
        <p className={styles.meaning}>
          We follow the same people over time. Each row starts with the people who were behind when they onboarded and shows where they are now. Most change takes
          more than a few months, so “No change yet” is normal this early.
        </p>
      </Card>
    </Reveal>
  )
}

const Legend = () => (
  <div className={styles.legend}>
    <span className={styles.legendItem}>
      <span className={`${styles.swatch} ${styles.swatchUp}`} />
      Moved up or now within the guide
    </span>
    <span className={styles.legendItem}>
      <span className={`${styles.swatch} ${styles.swatchSame}`} />
      No change yet
    </span>
    <span className={styles.legendItem}>
      <span className={`${styles.swatch} ${styles.swatchDown}`} />
      Moved down
    </span>
  </div>
)

// Counts of people, not averages. Only what the movement data measures; the
// design's contribution and spending-plan counts have no data yet. The debt
// count compares share of pay, not rands, so a raise alone can move someone.
const OutcomesCard = ({ data, highlighted, onAbout }: CardProps) => {
  const { cut_debt: cutDebt, moved_up_band: movedUp } = data.progress.changes

  return (
    <Reveal>
      <Card question="What has actually changed?" title="What has changed since they joined" onAbout={onAbout} highlighted={highlighted} footer={progressFooter(data)}>
        <p className={styles.metric}>Counts of people since the programme started</p>
        <div className={styles.tiles}>
          <Tile count={cutDebt} label={`${cutDebt === 1 ? 'person now puts' : 'people now put'} a smaller share of their pay towards debt`} />
          <Tile count={movedUp} label={`${peopleHave(movedUp ?? 0)} moved up at least one retirement band`} />
        </div>
        <p className={styles.meaning}>These count people, not averages: how many people’s figures moved this way between their first month and their latest.</p>
      </Card>
    </Reveal>
  )
}

// null when counted from fewer than 5 people.
const Tile = ({ count, label }: { count: number | null; label: string }) => (
  <div className={styles.tile}>
    <span className={styles.tileCount}>{count === null ? 'Hidden' : count}</span>
    <span className={styles.tileLabel}>{label}</span>
  </div>
)

const ProgressPanelContent = ({ panel, data }: { panel: Panel | null; data: FfpDashboardData }) => {
  if (panel === null) return null

  const { measures, changes } = data.progress
  const rows =
    panel === 'movement'
      ? ROWS.map((row) => movementCells(ROW_NAMES[row], measures[row]))
      : [
          ['Now put a smaller share of their pay towards debt', changes.cut_debt ?? 'Hidden: fewer than 5'],
          ['Moved up at least one retirement band', changes.moved_up_band ?? 'Hidden: fewer than 5'],
        ]
  const columns = panel === 'movement' ? ['Measure', 'Started behind', 'Moved up', 'No change', 'Moved down'] : ['Outcome', 'People']
  const { debt, spending, investing } = data.health.trends
  const benchmark =
    panel === 'movement'
      ? `Uses the same guides as the health cards: ${debt.guide}% for debt, ${spending.guide}% for spending, ${investing.guide}% for investing.`
      : 'There is no outside benchmark for this measure.'

  return (
    <>
      <PanelSection heading="What it means">
        <PanelMeaning panel={panel} />
      </PanelSection>
      <PanelSection heading="Who is included">
        <PanelIncluded
          included={`${data.progress.people_counted} people`}
          of={`of ${data.people.onboarded} onboarded`}
          exclusions={[
            {
              count: data.progress.not_yet_included,
              label: 'Not included yet: less than two months of data',
              detail: 'They’ll appear once they have two months of data.',
            },
          ]}
          everyone="Everyone onboarded has two months of data."
        />
      </PanelSection>
      <PanelSection heading="The numbers behind the card">
        <PanelTable columns={columns} rows={rows} note={COUNTS_NOTE} />
      </PanelSection>
      <PanelSection heading="Where the numbers come from">
        <PanelFreshness source={FRESHNESS_SOURCE} at={data.freshness.captured_at} />
        <PanelNote>Our data updates every Monday.</PanelNote>
      </PanelSection>
      <PanelSection heading="Benchmark">
        <PanelText>{benchmark}</PanelText>
      </PanelSection>
    </>
  )
}

const movementCells = (name: string, movement: Movement | null): (string | number)[] => {
  if (movement === null) return [name, 'Fewer than 5', 'Hidden', '', '']
  return [name, movement.started_behind, movement.improved, movement.no_change, movement.moved_down]
}

const PanelMeaning = ({ panel }: { panel: Panel }) => {
  if (panel === 'outcomes') {
    return (
      <>
        <PanelText>Counts of people whose figures moved between their first month and their latest, from their financial snapshots.</PanelText>
        <PanelText muted>Each person is counted once per outcome, however many times they did it.</PanelText>
      </>
    )
  }

  return (
    <>
      <PanelText>For each measure, we take the people who were behind in their first month and compare them with their latest month.</PanelText>
      <PanelText muted>
        “Moved up” means they moved up at least one band, or crossed to the right side of the guide. “Moved down” means the opposite.
      </PanelText>
    </>
  )
}
